/**
 * Settings → Audit Log tab. Two sub-tabs: "Mutations" (semantic
 * audit_log) and "API requests" (auto-captured request log). Both
 * gated server-side on users.manage.
 *
 * Filters: date range, actor type, action/path substring, status
 * range (request log only). Standard pagination.
 */
import React, { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import {
  type ApiRequestLogPage,
  type AuditLogPage,
  fetchApiRequestLog,
  fetchAuditLog,
} from "../../services/auditApi";

type Tab = "mutations" | "requests";
const PAGE_SIZE = 100;

export const AuditLogTab: React.FC = () => {
  const [tab, setTab] = useState<Tab>("mutations");

  return (
    <div className="space-y-3 max-w-6xl">
      <div className="flex gap-1 border-b border-secondary-200">
        {(["mutations", "requests"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              "px-3 py-1.5 text-sm -mb-px border-b-2 " +
              (tab === t
                ? "border-primary-500 text-primary-700 font-semibold"
                : "border-transparent text-secondary-600 hover:text-secondary-900")
            }
          >
            {t === "mutations" ? "Mutations" : "API requests"}
          </button>
        ))}
      </div>
      {tab === "mutations" ? <MutationsView /> : <RequestsView />}
    </div>
  );
};

export default AuditLogTab;

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

interface SharedFilterState {
  since: string;
  until: string;
  actorKind: string;
}

const SharedFilters: React.FC<{
  state: SharedFilterState;
  setState: (s: SharedFilterState) => void;
}> = ({ state, setState }) => (
  <>
    <label className="text-xs text-secondary-600">
      <div>Since (UTC)</div>
      <input
        type="date"
        value={state.since}
        onChange={(e) => setState({ ...state, since: e.target.value })}
        className="border border-secondary-300 rounded px-2 py-1 text-sm"
      />
    </label>
    <label className="text-xs text-secondary-600">
      <div>Until (UTC)</div>
      <input
        type="date"
        value={state.until}
        onChange={(e) => setState({ ...state, until: e.target.value })}
        className="border border-secondary-300 rounded px-2 py-1 text-sm"
      />
    </label>
    <label className="text-xs text-secondary-600">
      <div>Actor kind</div>
      <select
        value={state.actorKind}
        onChange={(e) => setState({ ...state, actorKind: e.target.value })}
        className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
      >
        <option value="">— Any —</option>
        <option value="user">user</option>
        <option value="api_key">api_key</option>
        <option value="anonymous">anonymous</option>
        <option value="system">system</option>
      </select>
    </label>
  </>
);

// ---------------------------------------------------------------------------
// Mutations view
// ---------------------------------------------------------------------------

const MutationsView: React.FC = () => {
  const { token } = useAuth();
  const [filters, setFilters] = useState<SharedFilterState>({
    since: "", until: "", actorKind: "",
  });
  const [action, setAction] = useState("");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<AuditLogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const p = await fetchAuditLog(token, {
        since: filters.since || undefined,
        until: filters.until || undefined,
        actor_kind: filters.actorKind || undefined,
        action: action || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, filters, action, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3">
        <SharedFilters state={filters} setState={(s) => { setFilters(s); setOffset(0); }} />
        <label className="text-xs text-secondary-600 flex-1 min-w-[140px]">
          <div>Action prefix</div>
          <input
            type="text"
            value={action}
            onChange={(e) => { setAction(e.target.value); setOffset(0); }}
            placeholder="e.g. item. or plug."
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
      </div>
      {error && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>}
      <Pager page={page} offset={offset} setOffset={setOffset} loading={loading} />
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Target</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && !page ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-secondary-500">loading…</td></tr>
            ) : page && page.rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-secondary-500">No audit rows match.</td></tr>
            ) : (
              page?.rows.flatMap((r) => {
                const out = [
                  <tr key={r.audit_id} className="hover:bg-secondary-50 align-top">
                    <td className="px-3 py-1.5 text-secondary-700">{fmtUtc(r.occurred_utc)}</td>
                    <td className="px-3 py-1.5">
                      <div className="font-medium">{r.actor_name ?? "—"}</div>
                      <div className="text-[10px] text-secondary-500">{r.actor_kind}{r.actor_id != null ? ` #${r.actor_id}` : ""}</div>
                    </td>
                    <td className="px-3 py-1.5 font-mono">{r.action}</td>
                    <td className="px-3 py-1.5 text-secondary-700">
                      {r.target_kind ? `${r.target_kind}:${r.target_id ?? "—"}` : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-secondary-500">{r.ip ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {r.payload_json && (
                        <button
                          type="button"
                          className="text-primary-600 text-xs hover:underline"
                          onClick={() => setExpandedId(expandedId === r.audit_id ? null : r.audit_id)}
                        >
                          {expandedId === r.audit_id ? "hide" : "details"}
                        </button>
                      )}
                    </td>
                  </tr>
                ];
                if (expandedId === r.audit_id && r.payload_json) {
                  out.push(
                    <tr key={`${r.audit_id}-payload`}>
                      <td colSpan={6} className="px-3 pb-2 bg-secondary-50">
                        <pre className="text-[11px] text-secondary-700 whitespace-pre-wrap break-all">
                          {(() => {
                            try { return JSON.stringify(JSON.parse(r.payload_json), null, 2); }
                            catch { return r.payload_json; }
                          })()}
                        </pre>
                      </td>
                    </tr>
                  );
                }
                return out;
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Requests view
// ---------------------------------------------------------------------------

const RequestsView: React.FC = () => {
  const { token } = useAuth();
  const [filters, setFilters] = useState<SharedFilterState>({
    since: "", until: "", actorKind: "",
  });
  const [pathQ, setPathQ] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<ApiRequestLogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const p = await fetchApiRequestLog(token, {
        since: filters.since || undefined,
        until: filters.until || undefined,
        actor_kind: filters.actorKind || undefined,
        path: pathQ || undefined,
        min_status: errorsOnly ? 400 : undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, filters, pathQ, errorsOnly, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3">
        <SharedFilters state={filters} setState={(s) => { setFilters(s); setOffset(0); }} />
        <label className="text-xs text-secondary-600 flex-1 min-w-[200px]">
          <div>Path prefix</div>
          <input
            type="text"
            value={pathQ}
            onChange={(e) => { setPathQ(e.target.value); setOffset(0); }}
            placeholder="e.g. /api/admin/ or /api/v1/"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-secondary-700 flex items-center gap-1.5 ml-1">
          <input
            type="checkbox"
            checked={errorsOnly}
            onChange={(e) => { setErrorsOnly(e.target.checked); setOffset(0); }}
          />
          Errors only (4xx/5xx)
        </label>
      </div>
      {error && <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>}
      <Pager page={page} offset={offset} setOffset={setOffset} loading={loading} />
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Method</th>
              <th className="text-left px-3 py-2">Path</th>
              <th className="text-right px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">ms</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && !page ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-secondary-500">loading…</td></tr>
            ) : page && page.rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-secondary-500">No request rows match.</td></tr>
            ) : (
              page?.rows.map((r) => (
                <tr key={r.request_id} className="hover:bg-secondary-50">
                  <td className="px-3 py-1.5 text-secondary-700">{fmtUtc(r.occurred_utc)}</td>
                  <td className="px-3 py-1.5 font-mono">{r.method}</td>
                  <td className="px-3 py-1.5 font-mono truncate max-w-[400px]" title={r.path}>{r.path}</td>
                  <td className={"px-3 py-1.5 text-right font-mono " + (r.status >= 500 ? "text-error-700" : r.status >= 400 ? "text-warning-700" : "")}>
                    {r.status}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-secondary-500">{r.latency_ms ?? "—"}</td>
                  <td className="px-3 py-1.5">
                    <div className="font-medium">{r.actor_name ?? "—"}</div>
                    <div className="text-[10px] text-secondary-500">{r.actor_kind}{r.actor_id != null ? ` #${r.actor_id}` : ""}</div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-secondary-500">{r.ip ?? "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared pager
// ---------------------------------------------------------------------------

const Pager: React.FC<{
  page: { total: number; rows: unknown[] } | null;
  offset: number;
  setOffset: (n: number) => void;
  loading: boolean;
}> = ({ page, offset, setOffset, loading }) => {
  if (!page) return null;
  return (
    <div className="flex items-center justify-between text-xs text-secondary-600">
      <span>
        <strong>{page.total.toLocaleString()}</strong> row
        {page.total === 1 ? "" : "s"}
        {page.total > 0 && (
          <>
            {" "}— showing {offset + 1}–{Math.min(offset + page.rows.length, page.total)}
          </>
        )}
      </span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm"
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          disabled={offset === 0 || loading}>← Prev</Button>
        <Button variant="outline" size="sm"
          onClick={() => setOffset(offset + PAGE_SIZE)}
          disabled={offset + PAGE_SIZE >= page.total || loading}>Next →</Button>
      </div>
    </div>
  );
};
